<template>
  <div class="b44-page">
    <div v-if="loading" class="b44-loading"><div class="b44-spinner"></div></div>
    <div v-else-if="!projeto" style="padding:3rem;text-align:center;color:#64748b">Projeto não encontrado.</div>
    <template v-else>
      <div v-if="!embedded" class="b44-header">
        <router-link class="b44-link" :to="{ name: 'portfolioProjetosAuditor' }">
          <i class="bi bi-chevron-left"></i> Voltar ao Portfólio
        </router-link>
        <div style="display:flex;gap:0.75rem;flex-wrap:wrap;align-items:center">
          <DocLink from="detalhe" :projeto-id="projeto.id" />
          <button type="button" class="b44-btn" :disabled="exportandoPdf" @click="baixarPdf">
            <i class="bi bi-file-earmark-arrow-down"></i>
            {{ exportandoPdf ? "Gerando..." : "Baixar Resumo PDF" }}
          </button>
          <router-link
            class="b44-btn primary"
            :to="{ name: 'projetoView', params: { idProjeto: projeto.id } }"
            target="_blank"
          >
            Abrir projeto
          </router-link>
        </div>
      </div>
      <div v-else class="b44-header" style="justify-content:flex-end">
        <button type="button" class="b44-btn" :disabled="exportandoPdf" @click="baixarPdf">
          <i class="bi bi-file-earmark-arrow-down"></i>
          {{ exportandoPdf ? "Gerando..." : "Baixar Resumo PDF" }}
        </button>
      </div>

      <div ref="resumoRef" class="b44-stack" style="margin-top:1.5rem">
        <div class="b44-card p-6">
          <div style="display:flex;justify-content:space-between;gap:1rem;flex-wrap:wrap">
            <div style="min-width:0;flex:1">
              <div style="display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;margin-bottom:0.35rem">
                <h1 style="margin:0;font-size:1.5rem;font-weight:700">
                  <span v-if="projeto.codigo" style="font-family:ui-monospace,monospace;font-size:1rem;color:#64748b;margin-right:0.5rem">{{ projeto.codigo }}</span>
                  {{ projeto.nome }}
                </h1>
                <HealthBadge :saude="projeto.saude" />
                <HelpTip tip="det_saude" />
              </div>
              <p v-if="projeto.descricao" style="font-size:0.875rem;color:#64748b;max-width:42rem;margin:0">{{ projeto.descricao }}</p>
              <div class="b44-meta">
                <span><i class="bi bi-person"></i> Gerente: <strong>{{ projeto.nome_gerente || "—" }}</strong><HelpTip tip="det_gerente" /></span>
                <span v-if="projeto.nome_responsavel">Assessor: <strong>{{ projeto.nome_responsavel }}</strong><HelpTip tip="det_assessor" /></span>
                <span v-if="projeto.nome_programa">Programa: <strong>{{ projeto.nome_programa }}</strong></span>
              </div>
              <div class="b44-meta">
                <span><i class="bi bi-calendar3"></i> Início: <strong>{{ fmtData(projeto.data_inicio) }}</strong><HelpTip tip="det_datas" /></span>
                <span><i class="bi bi-calendar3"></i> Previsão de Conclusão: <strong>{{ fmtData(projeto.data_fim_previsto) }}</strong></span>
                <span v-if="prazoInfo">
                  <i class="bi bi-calendar3"></i> Prazo:
                  <strong :style="{ color: prazoInfo.atrasado ? '#dc2626' : 'inherit' }">{{ prazoInfo.texto }}</strong>
                  <HelpTip tip="det_prazo" />
                </span>
                <span>
                  <i class="bi bi-currency-dollar"></i> Orçamento:
                  <strong>{{ projeto.orcamento > 0 ? fmtMoedaFull(projeto.orcamento) : "—" }}</strong>
                  <template v-if="projeto.custo_real > 0">
                    · Gasto:
                    <strong :style="{ color: gastoDanger ? '#dc2626' : 'inherit' }">{{ fmtMoedaFull(projeto.custo_real) }}</strong>
                    <span v-if="temDetalheCusto" style="color:#64748b;font-size:0.8rem;margin-left:0.35rem">
                      ({{ fmtMoedaFull(projeto.custo_pago) }} pago · {{ fmtMoedaFull(projeto.custo_a_pagar) }} a pagar<template v-if="(projeto.custo_despesas || 0) > 0"> · {{ fmtMoedaFull(projeto.custo_despesas) }} despesas</template>)
                    </span>
                  </template>
                  <HelpTip tip="det_orcamento" />
                </span>
                <span>Status: <strong>{{ statusLabels[projeto.status] || projeto.status }}</strong></span>
              </div>
            </div>
            <div style="display:flex;flex-direction:column;align-items:flex-end;gap:0.25rem">
              <div style="display:flex;align-items:center;gap:0.35rem">
                <MatrizBadge :classificacao="projeto.classificacao_matriz" size="md" />
                <HelpTip tip="det_matriz" />
              </div>
              <span style="font-family:ui-monospace,monospace;font-size:0.75rem;color:#64748b">
                P{{ projeto.score_planejamento ?? 0 }} · E{{ projeto.score_execucao ?? 0 }}
              </span>
              <div class="b44-nota-media">
                <p class="b44-nota-media__label">Nota Geral <HelpTip tip="det_nota_geral" /></p>
                <p class="b44-nota-media__valor" :class="toneNotaMedia">{{ notaMediaTexto }}</p>
                <p class="b44-nota-media__hint">média de planejamento e execução (0–100)</p>
              </div>
            </div>
          </div>
        </div>

        <div class="b44-grid-6">
          <div class="b44-kpi-card" :class="{ red: (projeto.resultados_atrasados || 0) > 0 }">
            <HelpTip tip="det_kpi_resultados" />
            <i class="bi bi-bullseye"></i>
            <p class="num">{{ projeto.total_resultados ?? (projeto.resultados || []).length }}</p>
            <p class="txt">
              Entregas ({{ projeto.resultados_validados ?? 0 }} validadas ·
              <span :class="{ danger: (projeto.resultados_atrasados || 0) > 0 }">{{ projeto.resultados_atrasados || 0 }} atrasadas</span>
              · {{ projeto.resultados_concluidos ?? 0 }} concluídas)
            </p>
          </div>
          <div class="b44-kpi-card" :class="{ red: (projeto.entregas_atrasadas || 0) > 0 }">
            <HelpTip tip="det_kpi_entregas" />
            <i class="bi bi-box-seam"></i>
            <p class="num">{{ projeto.total_entregas || 0 }}</p>
            <p class="txt">
              Pacotes de trabalho (
              <span :class="{ danger: (projeto.entregas_atrasadas || 0) > 0 }">{{ projeto.entregas_atrasadas || 0 }} em atraso</span>
              · {{ projeto.entregas_concluidas || 0 }} concluídos<template v-if="entregasSemTarefasCount > 0"> · {{ entregasSemTarefasCount }} sem tarefas</template>)
            </p>
          </div>
          <div class="b44-kpi-card" :class="{ red: (projeto.tarefas_atrasadas || 0) > 0 }">
            <HelpTip tip="det_kpi_tarefas" />
            <i class="bi bi-check2-square"></i>
            <p class="num">{{ projeto.total_tarefas || 0 }}</p>
            <p class="txt">
              Tarefas (
              <span :class="{ danger: (projeto.tarefas_atrasadas || 0) > 0 }">{{ projeto.tarefas_atrasadas || 0 }} atrasadas</span>
              · {{ projeto.tarefas_concluidas || 0 }} concluídas)
            </p>
            <div v-if="(projeto.total_tarefas || 0) > 0" class="b44-bar" style="margin-top:0.5rem">
              <i :style="{ width: pctTarefas + '%' }"></i>
            </div>
          </div>
          <div
            class="b44-kpi-card b44-kpi-tip"
            :class="{
              red: (projeto.dias_desatualizado || 0) > 30,
              amber: (projeto.dias_desatualizado || 0) > 15 && (projeto.dias_desatualizado || 0) <= 30,
            }"
          >
            <HelpTip tip="det_kpi_desatualizado" />
            <i class="bi bi-clock-history"></i>
            <p class="num">{{ projeto.dias_desatualizado ?? 0 }}</p>
            <p class="txt" :class="{ danger: (projeto.dias_desatualizado || 0) > 30, warn: (projeto.dias_desatualizado || 0) > 15 && (projeto.dias_desatualizado || 0) <= 30 }">
              Desatualizado ({{ projeto.dias_desatualizado ?? 0 }} dias sem atualização)
            </p>
            <p v-if="projeto.atualizacao_fonte_referencia || projeto.data_ultima_atualizacao" class="tip-hint">
              Ref.: {{ projeto.atualizacao_fonte_referencia || "atividade" }}
              <template v-if="projeto.data_ultima_atualizacao"> · {{ fmtData(projeto.data_ultima_atualizacao) }}</template>
            </p>
            <div v-if="atualizacaoFontes.length" class="b44-tip-panel" aria-hidden="true">
              <strong>Últimas atualizações consideradas</strong>
              <ul>
                <li v-for="(f, i) in atualizacaoFontes" :key="'af-' + i">
                  <span class="fonte">{{ f.fonte }}</span>
                  <span class="quando">{{ f.data_hora || fmtData(f.data) }}</span>
                </li>
              </ul>
            </div>
          </div>
          <div class="b44-kpi-card">
            <HelpTip tip="det_kpi_progresso" />
            <p class="num">{{ Number(projeto.progresso || 0).toFixed(2).replace(/\.00$/, '') }}%</p>
            <p class="txt">Progresso</p>
            <div class="b44-bar" style="margin-top:0.5rem;height:0.5rem"><i :style="{ width: (projeto.progresso || 0) + '%' }"></i></div>
          </div>
          <div class="b44-kpi-card">
            <HelpTip tip="det_kpi_reunioes" />
            <i class="bi bi-chat-left-text"></i>
            <p class="num">{{ projeto.total_reunioes ?? (projeto.reunioes || []).length }}</p>
            <p class="txt">Reuniões</p>
          </div>
        </div>

        <MatrizQuadranteCard :projeto="projeto" />

        <div class="b44-card b44-criterios-wrap">
          <div class="b44-bloco-head"><h2 class="b44-h2">Critérios <HelpTip tip="det_criterios" wide /></h2></div>
          <div class="b44-criterios-block">
            <CriteriosScorePanel
              titulo="Projeto"
              :classificacao="projeto.classificacao_matriz"
              :score-planejamento="projeto.score_planejamento ?? 0"
              :score-execucao="projeto.score_execucao ?? 0"
              :criterios="projeto.criterios_score || []"
            />
          </div>

          <div v-if="!(projeto.resultados || []).length" style="padding:1rem;color:#64748b;font-size:0.875rem">
            Nenhuma entrega sincronizada.
          </div>
          <div v-else class="b44-criterios-grid">
            <CriteriosScorePanel
              v-for="(res, idx) in resultadosOrdenados"
              :key="'res-cr-' + res.id"
              :titulo="'Entrega ' + ((res.indice ?? idx) + 1)"
              :classificacao="res.classificacao_matriz"
              :score-planejamento="res.score_planejamento ?? 0"
              :score-execucao="res.score_execucao ?? 0"
              :criterios="res.criterios_score || []"
              excluir-progresso
            />
          </div>
        </div>

        <div class="b44-card p-6">
          <div class="b44-bloco-head">
            <i class="bi bi-exclamation-triangle" style="color:var(--b44-primary)"></i>
            <h2 class="b44-h2">Impactos na Performance <HelpTip tip="det_impactos" /></h2>
          </div>
          <div v-if="impactos.length" class="b44-impacto">
            <div v-for="(imp, i) in impactos" :key="'imp' + i" class="b44-impacto-item" :class="imp.tone">
              <div style="display:flex;gap:0.5rem;align-items:flex-start">
                <i :class="imp.icon" style="margin-top:0.1rem"></i>
                <div style="min-width:0;flex:1">
                  <h4>{{ projeto.codigo ? projeto.codigo + ' ' : '' }}{{ imp.title }}</h4>
                  <p>{{ imp.desc }}</p>
                  <div v-if="imp.entregas && imp.entregas.length" style="margin-top:0.5rem">
                    <p style="font-size:0.7rem;font-weight:600;color:#64748b;margin:0 0 0.35rem">Pacotes de trabalho para ação de reparo</p>
                    <div v-for="e in imp.entregas" :key="'ie-' + e.id" style="font-size:0.75rem;padding:0.25rem 0;display:flex;gap:0.5rem;align-items:baseline">
                      <span class="b44-dot-score" :class="scoreTone(e.status === 'atrasada' || e._atrasada ? 0 : 5)"></span>
                      <span>{{ e.titulo || e.nome }}</span>
                      <span style="color:#64748b;margin-left:auto">{{ fmtData(e.data_prevista) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p v-else style="color:#64748b;font-size:0.875rem;margin:0">Nenhum impacto crítico identificado.</p>
        </div>

        <div class="b44-card" style="padding:0;overflow:hidden">
          <div class="b44-bloco-head" style="padding:0.75rem 1rem;margin:0;border-bottom:1px solid #e2e8f0">
            <i class="bi bi-bullseye" style="color:var(--b44-primary)"></i>
            <h2 class="b44-h2">Resumo do Projeto — Entregas, Pacotes de trabalho e Tarefas</h2>
            <span class="spacer b44-muted" style="font-size:0.875rem">
              {{ (projeto.resultados || []).length }} entrega(s) · {{ (projeto.entregas || []).length }} pacote(s) de trabalho
            </span>
          </div>
          <div style="overflow-x:auto">
            <table class="b44-resumo-table">
              <thead>
                <tr>
                  <th colspan="5" class="sec-res">Entrega</th>
                  <th colspan="5" class="sec-ent">Pacote de trabalho</th>
                </tr>
                <tr class="sub">
                  <th class="align-left">Nr Entrega</th>
                  <th class="align-left">Data Início</th>
                  <th>Prev. Conclusão</th>
                  <th>Data de Conclusão</th>
                  <th>Status</th>
                  <th class="ent-first">Nr Pacote</th>
                  <th>Data Início</th>
                  <th>Prev. Conclusão</th>
                  <th>Data de Conclusão</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="!resumoRows.length">
                  <tr>
                    <td colspan="10" style="text-align:center;padding:2rem;color:#64748b">
                      Nenhuma entrega ou pacote de trabalho sincronizado para este projeto.
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <tr v-for="(row, ri) in resumoRows" :key="'rr-' + ri" :class="{ 'res-start': row.showRes }">
                    <td v-if="row.showRes" class="res-cell align-left nr" :rowspan="row.rowSpan">{{ row.resNr }}</td>
                    <td v-if="row.showRes" class="res-cell align-left" :rowspan="row.rowSpan">{{ fmtData(row.resInicio) }}</td>
                    <td v-if="row.showRes" class="res-cell" :rowspan="row.rowSpan">{{ fmtData(row.resPrev) }}</td>
                    <td v-if="row.showRes" class="res-cell" :rowspan="row.rowSpan">{{ fmtData(row.resConc) }}</td>
                    <td v-if="row.showRes" class="res-cell" :rowspan="row.rowSpan">
                      <span class="b44-tag" :class="resultadoTagClass(row.resStatus)">{{ resultadoLabel(row.resStatus) }}</span>
                    </td>
                    <td class="ent-first">{{ row.entNr }}</td>
                    <td>{{ fmtData(row.entInicio) }}</td>
                    <td>{{ fmtData(row.entPrev) }}</td>
                    <td>{{ fmtData(row.entConc) }}</td>
                    <td>
                      <span v-if="row.entStatus" class="b44-tag" :class="entregaTagClass(row.entStatus)">{{ entregaLabel(row.entStatus) }}</span>
                      <span v-else>—</span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <ControleMudancaAuditor
          v-if="pcmView"
          :pcm="pcmView"
          :status-projeto="statusLabels[projeto.status] || projeto.status"
        />

        <div v-if="(projeto.reunioes || []).length" class="b44-card" style="padding:0;overflow:hidden">
          <div style="padding:0.85rem 1rem;border-bottom:1px solid #e2e8f0">
            <h2 class="b44-h2" style="margin:0">Reuniões do Projeto</h2>
          </div>
          <div
            v-for="r in projeto.reunioes"
            :key="r.id"
            style="display:flex;justify-content:space-between;gap:0.75rem;padding:0.75rem 1rem;border-bottom:1px solid #f1f5f9"
          >
            <div>
              <div style="font-weight:600;font-size:0.875rem">{{ r.titulo }}</div>
              <div style="font-size:0.75rem;color:#64748b">{{ r.tipo }}{{ r.tema ? ' · ' + r.tema : '' }}</div>
            </div>
            <div style="display:flex;align-items:center;gap:0.75rem">
              <span style="font-size:0.75rem;color:#64748b">{{ fmtData(r.data) }}</span>
              <span class="b44-tag" :class="r.status === 'cancelled' || r.status === 'cancelada' ? 'slate' : 'blue'">{{ r.status }}</span>
              <a v-if="r.html_link" :href="r.html_link" target="_blank" class="b44-link">Abrir</a>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import "@/assets/css/portfolio-auditor.css";
import servicePortfolioProjetos from "@/services/servicePortfolioProjetos";
import { getPermissao } from "@/services/permissao-service";
import { statusLabels, fmtMoeda } from "@/utils/roboflexLabels";
import HealthBadge from "@/components/PortfolioAuditor/HealthBadge.vue";
import MatrizBadge from "@/components/PortfolioAuditor/MatrizBadge.vue";
import HelpTip from "@/components/PortfolioAuditor/HelpTip.vue";
import DocLink from "@/components/PortfolioAuditor/DocLink.vue";
import MatrizQuadranteCard from "@/components/PortfolioAuditor/MatrizQuadranteCard.vue";
import CriteriosScorePanel from "@/components/PortfolioAuditor/CriteriosScorePanel.vue";
import ControleMudancaAuditor from "@/components/PortfolioAuditor/ControleMudancaAuditor.vue";
import { useApiError } from "@/composables/useApiError";
import { gerarResumoPdf } from "@/utils/gerarResumoPdf";

const RESULTADO_LABEL = {
  planejado: "Planejado",
  validado: "Validado",
  em_andamento: "Em Andamento",
  concluido: "Concluído",
  atrasado: "Atrasado",
  cancelado: "Cancelado",
  pendente: "Pendente",
};

const ENTREGA_LABEL = {
  planejada: "A iniciar",
  em_andamento: "Em Andamento",
  entregue: "Entregue",
  atrasada: "Atrasada",
  cancelada: "Cancelada",
};

export default {
  name: "PortfolioProjetoDetalheAuditor",
  components: {
    HealthBadge,
    MatrizBadge,
    MatrizQuadranteCard,
    CriteriosScorePanel,
    ControleMudancaAuditor,
    HelpTip,
    DocLink,
  },
  props: {
    id: { type: [String, Number], required: true },
    embedded: { type: Boolean, default: false },
  },
  setup() {
    const { handleError } = useApiError();
    return { handleApiError: handleError, fmtMoeda, statusLabels };
  },
  data() {
    return {
      loading: true,
      projeto: null,
      exportandoPdf: false,
    };
  },
  computed: {
    resultadosOrdenados() {
      return [...(this.projeto?.resultados || [])].sort((a, b) => (a.indice ?? 0) - (b.indice ?? 0));
    },
    pctTarefas() {
      const t = this.projeto?.total_tarefas || 0;
      if (!t) return 0;
      return Math.round(((this.projeto.tarefas_concluidas || 0) / t) * 100);
    },
    notaMedia() {
      const p = this.projeto || {};
      const media = ((Number(p.score_planejamento) || 0) + (Number(p.score_execucao) || 0)) / 2;
      return Number.isFinite(media) ? media * 10 : 0;
    },
    notaMediaTexto() {
      return String(Math.round(this.notaMedia));
    },
    toneNotaMedia() {
      if (this.notaMedia >= 70) return "ok";
      if (this.notaMedia >= 50) return "warn";
      return "bad";
    },
    entregasSemTarefasList() {
      const entregas = this.projeto?.entregas || [];
      return entregas.filter((e) => {
        const st = (e.status || "").toLowerCase();
        if (st.includes("conclu") || st.includes("entreg") || st.includes("cancel")) return false;
        return (e.total_tarefas || 0) === 0;
      });
    },
    entregasSemTarefasCount() {
      return this.entregasSemTarefasList.length;
    },
    gastoDanger() {
      const p = this.projeto;
      if (!p) return false;
      return (p.orcamento > 0 && p.custo_real > p.orcamento) || p.orcamento === 0;
    },
    atualizacaoFontes() {
      return Array.isArray(this.projeto?.atualizacao_fontes) ? this.projeto.atualizacao_fontes : [];
    },
    temDetalheCusto() {
      const p = this.projeto;
      if (!p) return false;
      return p.custo_pago != null || p.custo_a_pagar != null || p.custo_despesas != null;
    },
    prazoInfo() {
      const p = this.projeto;
      if (!p?.data_inicio && p?.dias_execucao == null) return null;

      const diasExecucao = p.dias_execucao != null
        ? p.dias_execucao
        : this.diasEntreDatas(p.data_inicio, null);
      const diasPrevistos = p.dias_previstos != null
        ? p.dias_previstos
        : (p.data_inicio && p.data_fim_previsto
          ? this.diasEntreDatas(p.data_inicio, p.data_fim_previsto)
          : null);

      // Sempre a mesma regra do dashboard / score (backend)
      const atrasado = !!p.atrasado;
      const diasAtraso = Number(p.dias_atraso || 0);

      if (diasExecucao == null && !atrasado) return null;

      return {
        atrasado,
        texto: `${diasExecucao ?? 0} dias${diasPrevistos != null ? ` de ${diasPrevistos} previstos` : ""}${atrasado ? ` — ATRASADO (${diasAtraso} dias)` : " — No Prazo"}`,
      };
    },
    temMudanca() {
      return !!this.pcmView;
    },
    pcmView() {
      const p = this.projeto;
      if (!p) return null;

      if (p.pcm && typeof p.pcm === "object") {
        const pcm = { ...p.pcm };
        if (!Array.isArray(pcm.resultados) || !pcm.resultados.length) {
          pcm.resultados = this.mapResultadosParaPcm(p.resultados);
        }
        return pcm;
      }

      const temSinal = !!(
        p.ata ||
        p.proposito_mudanca ||
        p.solicitante_nome ||
        p.parecer_responsavel ||
        p.custo_justificativa ||
        p.comite_aprovacao ||
        p.pcm_aprovada ||
        p.data_aprovacao_pcm ||
        (p.resultados || []).length
      );
      if (!temSinal) return null;

      const comite = Array.isArray(p.comite)
        ? p.comite
        : String(p.comite_aprovacao || "")
            .split(",")
            .map((n) => n.trim())
            .filter(Boolean)
            .map((name, i) => ({ id: i + 1, name }));

      return {
        id: p.pcm_id || null,
        codigo: p.codigo || "",
        aprovada: !!p.pcm_aprovada,
        status: p.pcm_aprovada ? "Aprovado" : "Não aprovado",
        dt_prevista_conclusao: p.data_fim_previsto || null,
        dt_aprovacao: p.data_aprovacao_pcm || null,
        solicitante_nome: p.solicitante_nome || "",
        gerente_nome: p.nome_gerente || "",
        estimativa_custo: p.orcamento || 0,
        custo_previsto_resultados: 0,
        proposito_mudanca: p.proposito_mudanca || "",
        parecer_responsavel: p.parecer_responsavel || "",
        custo_justificativa: p.custo_justificativa || "",
        comite,
        comite_aprovacao: p.comite_aprovacao || "",
        resultados: this.mapResultadosParaPcm(p.resultados),
        anexos: [],
        pedidos_mudanca: Array.isArray(p.pedidos_mudanca) ? p.pedidos_mudanca : [],
        ata: p.ata || "",
      };
    },
    entregasAtrasadasList() {
      const hoje = this.hojeLocal();
      return (this.projeto?.entregas || []).filter((e) => {
        const st = (e.status || "").toLowerCase();
        if (st.includes("conclu") || st.includes("entreg") || st.includes("cancel") || e.dt_conclusao) return false;
        if (!e.data_prevista) return false;
        const prev = this.parseDiaLocal(e.data_prevista);
        return prev && prev < hoje;
      }).map((e) => ({ ...e, _atrasada: true }));
    },
    entregasSemTarefas() {
      // Backend não envia tarefas detalhadas; usa flag agregada quando disponível
      return [];
    },
    resumoRows() {
      const resultados = this.resultadosOrdenados;
      const entregas = this.projeto?.entregas || [];
      const byRes = {};
      for (const e of entregas) {
        const key = e.resultado_id;
        if (!key) continue;
        if (!byRes[key]) byRes[key] = [];
        byRes[key].push(e);
      }
      const rows = [];
      for (const [idx, r] of resultados.entries()) {
        const ents = byRes[r.id] || [];
        const rowSpan = Math.max(ents.length, 1);
        if (!ents.length) {
          rows.push({
            showRes: true,
            rowSpan: 1,
            resNr: (r.indice ?? idx) + 1,
            resInicio: r.data_inicio || null,
            resPrev: r.dt_prevista,
            resConc: r.dt_conclusao,
            resStatus: r.status,
            entNr: "—",
            entInicio: null,
            entPrev: null,
            entConc: null,
            entStatus: null,
          });
        } else {
          ents.forEach((e, ei) => {
            rows.push({
              showRes: ei === 0,
              rowSpan,
              resNr: (r.indice ?? idx) + 1,
              resInicio: r.data_inicio || null,
              resPrev: r.dt_prevista,
              resConc: r.dt_conclusao,
              resStatus: r.status,
              entNr: e.indice != null ? e.indice + 1 : ei + 1,
              entInicio: e.data_inicio || null,
              entPrev: e.data_prevista,
              entConc: e.dt_conclusao || e.data_entrega,
              entStatus: e.status,
            });
          });
        }
      }
      const sem = entregas.filter((e) => !e.resultado_id);
      sem.forEach((e, ei) => {
        rows.push({
          showRes: true,
          rowSpan: 1,
          resNr: "—",
          resInicio: null,
          resPrev: null,
          resConc: null,
          resStatus: null,
          entNr: e.indice != null ? e.indice + 1 : ei + 1,
          entInicio: e.data_inicio || null,
          entPrev: e.data_prevista,
          entConc: e.dt_conclusao || e.data_entrega,
          entStatus: e.status,
        });
      });
      return rows;
    },
    impactos() {
      const p = this.projeto;
      if (!p) return [];
      const list = [];

      if ((p.dias_desatualizado || 0) > 30) {
        list.push({
          tone: "red",
          icon: "bi bi-clock",
          title: "Status desatualizado",
          desc: `Há ${p.dias_desatualizado} dias sem atualização. Gerente deve ser convocado para atualizar o sistema.`,
        });
      } else if ((p.dias_desatualizado || 0) > 15) {
        list.push({
          tone: "amber",
          icon: "bi bi-clock",
          title: "Status recente",
          desc: `Há ${p.dias_desatualizado} dias sem atualização. Considerar revisão na próxima reunião de portfólio.`,
        });
      }

      if (p.atrasado) {
        list.push({
          tone: "red",
          icon: "bi bi-exclamation-circle",
          title: "Prazo vencido",
          desc: `Prazo era ${this.fmtData(p.data_fim_previsto)} (${p.dias_atraso || 0} dias de atraso). Necessita replanejamento ou ação corretiva.`,
          entregas: this.entregasAtrasadasList.slice(0, 8),
        });
      } else if (p.data_fim_previsto && p.status !== "concluido" && p.status !== "cancelado") {
        const diasRestantes = this.diasEntreDatas(null, p.data_fim_previsto);
        if (diasRestantes != null && diasRestantes >= 0 && diasRestantes < 30) {
          list.push({
            tone: "amber",
            icon: "bi bi-calendar-event",
            title: "Prazo próximo",
            desc: `Prazo em ${this.fmtData(p.data_fim_previsto)} (${diasRestantes} dias restantes). Verificar progresso dos pacotes de trabalho.`,
          });
        }
      }

      if (!p.tem_entregas && p.status === "em_andamento") {
        list.push({
          tone: "red",
          icon: "bi bi-file-earmark-x",
          title: "Sem pacotes de trabalho definidos",
          desc: "Projeto em andamento sem pacotes de trabalho cadastrados. Necessita plano de pacotes de trabalho para acompanhamento.",
        });
      }
      if (!p.tem_tarefas && p.status === "em_andamento") {
        list.push({
          tone: "amber",
          icon: "bi bi-file-earmark-x",
          title: "Sem tarefas",
          desc: "Projeto em andamento sem tarefas. Coordenador deve desdobrar os pacotes de trabalho em tarefas.",
        });
      }
      if (this.entregasSemTarefasList.length > 0) {
        const pendentes = (p.entregas || []).filter((e) => {
          const st = (e.status || "").toLowerCase();
          return !st.includes("conclu") && !st.includes("entreg") && !st.includes("cancel");
        }).length;
        list.push({
          tone: "orange",
          icon: "bi bi-file-earmark-x",
          title: "Pacotes de trabalho sem tarefas",
          desc: `${this.entregasSemTarefasList.length} de ${pendentes || this.entregasSemTarefasList.length} pacote(s) de trabalho pendente(s) não possui(m) tarefas vinculadas. Necessita desdobramento em tarefas para execução e acompanhamento.`,
          entregas: this.entregasSemTarefasList.slice(0, 8),
        });
      }

      if (p.estourou_custo || (p.custo_real > p.orcamento && p.orcamento > 0)) {
        const pct = p.percentual_custo || Math.round((p.custo_real / p.orcamento) * 100);
        list.push({
          tone: "red",
          icon: "bi bi-currency-dollar",
          title: "Orçamento ultrapassado",
          desc: `Custo real (${pct}% do orçamento) ultrapassou o previsto. ${fmtMoeda(p.custo_real)} vs ${fmtMoeda(p.orcamento)}.`,
        });
      } else if (p.orcamento === 0 && p.status === "em_andamento") {
        list.push({
          tone: "amber",
          icon: "bi bi-currency-dollar",
          title: "Sem orçamento",
          desc: "Projeto sem orçamento definido. Necessita estimativa para controle financeiro.",
        });
      }

      return list;
    },
  },
  watch: {
    id: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    mapResultadosParaPcm(resultados) {
      return [...(resultados || [])]
        .sort((a, b) => (a.indice ?? 0) - (b.indice ?? 0))
        .map((r) => ({
          id: r.id,
          descricao: r.descricao || "",
          evidencias: r.evidencias || "",
          custo: r.sem_custo ? 0 : (r.custo_previsto ?? r.custo ?? 0),
          sem_custo: !!r.sem_custo,
          dt_prevista_conclusao: r.dt_prevista_conclusao || r.dt_prevista || null,
          dt_conclusao: r.dt_conclusao || null,
          status: r.status || "pendente",
          indice: r.indice ?? 0,
        }));
    },
    async load() {
      this.loading = true;
      if (!this.embedded) {
        const funcs = await getPermissao();
        if (!funcs.includes(116)) {
          this.$router.replace("/");
          return;
        }
      }
      try {
        this.projeto = await servicePortfolioProjetos.detalhe(this.id);
      } catch (e) {
        this.handleApiError(e);
        this.projeto = null;
      } finally {
        this.loading = false;
      }
    },
    async baixarPdf() {
      if (!this.projeto || this.exportandoPdf) return;
      this.exportandoPdf = true;
      try {
        const codigo = this.projeto.codigo || this.projeto.id;
        const titulo = `${this.projeto.codigo ? this.projeto.codigo + " " : ""}${this.projeto.nome || "Projeto"}`.trim();
        await gerarResumoPdf(this.$refs.resumoRef, `Resumo_${codigo}.pdf`, titulo);
      } catch (e) {
        this.handleApiError(e);
      } finally {
        this.exportandoPdf = false;
      }
    },
    fmtData(d) {
      const date = this.parseDiaLocal(d);
      return date ? date.toLocaleDateString("pt-BR") : "—";
    },
    /** Interpreta Y-m-d como data de calendário local (evita deslocar 1 dia por UTC). */
    parseDiaLocal(d) {
      if (!d) return null;
      const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      const date = new Date(d);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    hojeLocal() {
      const n = new Date();
      return new Date(n.getFullYear(), n.getMonth(), n.getDate());
    },
    /**
     * Diferença em dias de calendário.
     * de=null → hoje; ate=null → hoje.
     */
    diasEntreDatas(de, ate) {
      const inicio = de == null ? this.hojeLocal() : this.parseDiaLocal(de);
      const fim = ate == null ? this.hojeLocal() : this.parseDiaLocal(ate);
      if (!inicio || !fim) return null;
      return Math.round((fim - inicio) / 86400000);
    },
    fmtMoedaFull(v) {
      return (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    scoreTone(score) {
      if (score >= 7) return "ok";
      if (score >= 4) return "mid";
      return "bad";
    },
    entregaLabel(st) {
      if (!st) return "—";
      const key = String(st).toLowerCase().replace(/\s+/g, "_");
      if (ENTREGA_LABEL[key]) return ENTREGA_LABEL[key];
      if (key.includes("conclu") || key.includes("entreg")) return "Entregue";
      if (key.includes("cancel")) return "Cancelada";
      if (key.includes("andamento")) return "Em Andamento";
      if (key.includes("atras")) return "Atrasada";
      if (key.includes("planej") || key.includes("iniciar")) return "A iniciar";
      return st;
    },
    entregaTagClass(st) {
      const key = String(st || "").toLowerCase();
      if (key.includes("entreg") || key.includes("conclu")) return "green";
      if (key.includes("atras")) return "red";
      if (key.includes("cancel")) return "slate";
      if (key.includes("andamento")) return "blue";
      return "amber";
    },
    resultadoLabel(st) {
      if (!st) return "—";
      const key = String(st).toLowerCase();
      if (RESULTADO_LABEL[key]) return RESULTADO_LABEL[key];
      if (key.includes("conclu")) return "Concluído";
      if (key.includes("valid")) return "Validado";
      if (key.includes("cancel")) return "Cancelado";
      if (key.includes("andamento")) return "Em Andamento";
      if (key.includes("atras")) return "Atrasado";
      if (key.includes("planej") || key === "pendente") return "Planejado";
      return st;
    },
    resultadoTagClass(st) {
      const key = String(st || "").toLowerCase();
      if (key.includes("conclu")) return "green";
      if (key.includes("valid")) return "purple";
      if (key.includes("atras")) return "red";
      if (key.includes("cancel")) return "slate";
      if (key.includes("andamento")) return "blue";
      return "amber";
    },
  },
};
</script>

<style scoped>
.b44-resumo-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}
.b44-resumo-table th,
.b44-resumo-table td {
  padding: 0.4rem 0.5rem;
  text-align: center;
  border-right: 1px solid rgba(226, 232, 240, 0.5);
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  white-space: nowrap;
  color: #64748b;
  vertical-align: top;
}
.b44-resumo-table th.sec-res { background: #eff6ff; color: #1d4ed8; font-weight: 600; }
.b44-resumo-table th.sec-ent { background: #fffbeb; color: #b45309; font-weight: 600; border-right: 0; }
.b44-resumo-table tr.sub th { background: #f8fafc; font-weight: 500; }
.b44-resumo-table th.align-left,
.b44-resumo-table td.align-left { text-align: left; }
.b44-resumo-table td:last-child,
.b44-resumo-table th:last-child { border-right: 0; }

/* Agrupamento por resultado: cada bloco é delimitado por uma linha mais forte. */
.b44-resumo-table tbody tr.res-start > td { border-top: 2px solid #cbd5e1; }
.b44-resumo-table tbody tr:first-child > td { border-top: 0; }
.b44-resumo-table td.res-cell {
  background: #f8fafc;
  vertical-align: middle;
  border-right-color: #cbd5e1;
}
.b44-resumo-table td.res-cell.nr { font-weight: 600; color: #0f172a; }
.b44-resumo-table th.sec-ent,
.b44-resumo-table th.ent-first,
.b44-resumo-table td.ent-first { border-left: 2px solid #cbd5e1; }
@media (max-width: 900px) {
  .b44-criterios-grid { grid-template-columns: 1fr !important; }
}

:global(.dark) .b44-resumo-table th,
:global(.dark) .b44-resumo-table td {
  border-right-color: rgba(148, 163, 184, 0.22);
  border-bottom-color: rgba(148, 163, 184, 0.22);
  color: #94a3b8;
}
:global(.dark) .b44-resumo-table th.sec-res { background: rgba(59, 130, 246, 0.16); color: #93c5fd; }
:global(.dark) .b44-resumo-table th.sec-ent { background: rgba(245, 158, 11, 0.16); color: #fcd34d; }
:global(.dark) .b44-resumo-table tr.sub th { background: rgba(255, 255, 255, 0.04); }
:global(.dark) .b44-resumo-table tbody tr.res-start > td { border-top-color: rgba(148, 163, 184, 0.45); }
:global(.dark) .b44-resumo-table td.res-cell {
  background: rgba(255, 255, 255, 0.04);
  border-right-color: rgba(148, 163, 184, 0.4);
}
:global(.dark) .b44-resumo-table td.res-cell.nr { color: #e8eef6; }
:global(.dark) .b44-resumo-table th.sec-ent,
:global(.dark) .b44-resumo-table th.ent-first,
:global(.dark) .b44-resumo-table td.ent-first { border-left-color: rgba(148, 163, 184, 0.4); }
</style>
